import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { UserCircle } from "@phosphor-icons/react";
import { SectionEyebrow, SectionTitle, FadeUp, HRule } from "./_shared";
import GoldFoilFrame from "./GoldFoilFrame";

export const TEAM = [
  {
    id: "john-campetella",
    name: "John Campetella",
    role: "Founder · CEO",
    credential:
      "Founder/CEO Integrated Wellness Consulting, LLC · Adjunct/Associate Professor · UFC Veteran · NPC Bodybuilder · Author · Inventor",
    image: "/team/john-campetella.jpg",
    bio: [
      "Founder and CEO of Integrated Wellness Consulting, LLC, and the architect behind the Unified Medical Model and the Integrated Matrix Engine.",
      "Adjunct/Associate Professor of Anatomy and Physiology, Biology, Microbiology, Pharmacology, and Nutrition — combining decades of teaching with deep clinical and performance science.",
      "UFC veteran, NPC bodybuilder, and physique competitor — bringing real-world experience of the body under pressure, adaptation, and recovery into every protocol he designs.",
      "Author of The Integrated Fat Loss Blueprint: A Muscle-Centric Approach, The Integrated Guide to Functional Medicine, The Integrated Longevity Blueprint, The Integrated Guide to Heart Health, and The Unified Medical Model.",
      "Inventor of five patent-pending technologies: the Integrated Matrix Engine, the Multi-Tissue Biometric Engine, the Wave-Harmonic Model of EKGs & EEGs, the Multi-Tissue Photometric Engine, and the Body Composition & Metabolic Risk Engine™.",
    ],
    bioPending: false,
    storyHref: "/founder-story",
  },
  {
    id: "michael-ellis",
    name: "Dr. Michael Ellis, DO",
    role: "Medical Director",
    credential: "Internal Medicine · Wellness & Aesthetics",
    image: "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/rmekuye4_Mike%20Ellis.png",
    bio: [
      "Dr. Michael Ellis, DO, is a licensed physician clinically trained in internal medicine. He completed his undergraduate studies at Penn State (Kinesiology), earned his medical degree at the Virginia College of Osteopathic Medicine, and finished his internal medicine residency at Saint Michael's Medical Center in Newark, NJ.",
      "After years on the front lines as a hospitalist — including Hackensack Meridian Riverview Medical Center, Monument Health, and Sanford Health Systems — he shifted his focus to wellness, longevity, and aesthetic medicine.",
      "He also serves as Chief Medical Officer of NFL Alumni Performance Labs (NFLAPL), Lead Investigator at Evolutionary Biologics, and CEO of Evoú Body Contouring.",
    ],
    bioPending: false,
  },
  {
    id: "michael-fullen",
    name: "Michael Fullen",
    role: "IT Consultant",
    credential: "SVP & Chief Information Officer · M.S. Management of Information Systems, NJIT",
    image: "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/gg2qlxh4_Michael%20Fullen.jpg",
    bio: [
      "Michael Fullen is Senior Vice President and Chief Information Officer at Toa Re America, where he leads enterprise technology, security, and data infrastructure for one of the world's largest reinsurance organizations.",
      "He holds a Master of Science in Management of Information Systems from the New Jersey Institute of Technology (NJIT) and brings two decades of senior IT leadership across regulated, data-sensitive enterprise environments.",
      "As technology adviser to Integrated Wellness, Michael oversees infrastructure, security architecture, and the standards that protect patient data across every system we operate.",
    ],
    bioPending: false,
  },
  {
    id: "giovanni-boiano",
    name: "Dr. Giovanni Boiano, DDS",
    role: "Dental Medicine Adviser",
    credential: "FAGD · FICOI · Implant & Cosmetic Dentistry",
    image: "/team/giovanni-boiano.png",
    bio: [
      "Dr. Boiano earned his Bachelor of Arts from New York University College of Arts and Science before receiving his DDS from New York University College of Dentistry. He completed a one-year general practice residency at the Level 1 trauma center of Westchester Medical Center, followed by a two-year surgical and prosthetic implant certificate program at NYU.",
      "He holds fellowship credentials in the Academy of General Dentistry (FAGD) and the International Congress of Oral Implantologists (FICOI), with continuing education in advanced bone and gum grafting, orthodontics, endodontics, and cosmetic dentistry.",
      "Dr. Boiano maintains a teaching role at Westchester Medical Center, working with general practice and oral & maxillofacial surgery residents. He is one of the few US dentists operating a dedicated dental lab — Digital Dental Designs — fabricating CAD/CAM implant, cosmetic, and esthetic restorations.",
    ],
    bioPending: false,
  },
  {
    id: "christina-decicco",
    name: "Christina DeCicco, FNP-C",
    role: "Nurse Practitioner",
    credential: "Board-Certified Family Nurse Practitioner",
    image: "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/w514ewqt_Christina.jpg",
    bio: [
      "Christina DeCicco is a board-certified Family Nurse Practitioner (FNP-C) practicing in Staten Island, New York. She works across the full primary-care spectrum — diagnosing and treating patients of all ages, with a focus on prevention, chronic-condition management, and continuity of care.",
      "Her clinical role on the Integrated Wellness team brings a hands-on family-medicine perspective to every patient program — making sure that lifestyle, longevity, and optimization plans are grounded in safe, day-to-day clinical practice.",
    ],
    bioPending: false,
  },
];

function Avatar({ image, name }) {
  if (image) {
    return (
      <GoldFoilFrame rounded="full" inset={3}>
        <img
          src={image}
          alt={`${name} — Integrated Clinical Intelligence`}
          className="h-24 w-24 md:h-28 md:w-28 rounded-full object-cover bg-card block"
          loading="lazy"
        />
      </GoldFoilFrame>
    );
  }
  return (
    <GoldFoilFrame rounded="full" inset={3}>
      <div className="h-24 w-24 md:h-28 md:w-28 rounded-full bg-card grid place-items-center text-primary/55">
        <UserCircle size={56} weight="duotone" />
      </div>
    </GoldFoilFrame>
  );
}

export default function Team() {
  return (
    <section
      id="team"
      data-testid="team-section"
      className="py-24 md:py-36 bg-background"
    >
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <FadeUp className="text-center">
          <SectionEyebrow>Leadership &amp; Clinical Team</SectionEyebrow>
          <SectionTitle className="mt-5">
            The people behind <em className="not-italic text-primary">your care</em>.
          </SectionTitle>
          <p className="mt-7 max-w-2xl mx-auto text-lg md:text-xl text-foreground/80 leading-relaxed">
            Clinicians, advisers, and operators committed to the long-term health of every
            patient we serve.
          </p>
        </FadeUp>

        <HRule />

        <div className="space-y-12 md:space-y-14">
          {TEAM.map((person, i) => (
            <motion.article
              key={person.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.04 }}
              data-testid={`team-${person.id}`}
              className="grid grid-cols-12 gap-5 md:gap-8 items-start pb-12 md:pb-14 border-b border-border last:border-b-0 last:pb-0"
            >
              <div className="col-span-12 md:col-span-3 flex md:block justify-center">
                <Avatar image={person.image} name={person.name} />
              </div>
              <div className="col-span-12 md:col-span-9">
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-primary/75 mb-2">
                  {person.role}
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight leading-tight">
                  {person.name}
                </h3>
                <div className="mt-1 text-sm md:text-base text-foreground/55 italic">
                  {person.credential}
                </div>
                <div className="mt-4">
                  {person.bioPending ? (
                    <p className="text-base text-foreground/55 italic">
                      Bio coming soon.
                    </p>
                  ) : (
                    <>
                      <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
                        {person.bio[0]}
                      </p>
                      {person.bio.length > 1 && (
                        <details className="group/bio mt-3">
                          <summary
                            data-testid={`team-${person.id}-bio-toggle`}
                            className="cursor-pointer list-none inline-flex items-center gap-2 py-1 font-mono text-[0.62rem] uppercase tracking-[0.2em] text-primary font-semibold"
                          >
                            Full bio
                            <span
                              aria-hidden
                              className="text-accent font-bold text-base leading-none group-open/bio:rotate-45 transition-transform"
                            >
                              +
                            </span>
                          </summary>
                          <div className="mt-3 space-y-3">
                            {person.bio.slice(1).map((para) => (
                              <p
                                key={para.slice(0, 40)}
                                className="text-base md:text-lg text-foreground/80 leading-relaxed"
                              >
                                {para}
                              </p>
                            ))}
                          </div>
                        </details>
                      )}
                    </>
                  )}
                </div>
                {person.storyHref && (
                  <Link
                    to={person.storyHref}
                    data-testid={`team-${person.id}-story-link`}
                    className="mt-5 inline-flex items-center gap-1.5 font-display text-base font-semibold text-primary hover:gap-2.5 transition-all"
                  >
                    Read his full story <span aria-hidden>→</span>
                  </Link>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
